
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Download, Facebook, Instagram, Linkedin, Download as TikTok, Link2, Copy } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";

const Media = () => {
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const navigate = useNavigate();

  const { data: profile, isError, error } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error("Session error:", sessionError);
        throw sessionError;
      }

      if (!session) {
        console.log("No active session");
        navigate('/login');
        return null;
      }

      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', session.user.id)
        .maybeSingle();
      
      if (profileError) {
        console.error("Profile error:", profileError);
        throw profileError;
      }
      
      return data;
    },
    retry: false,
    meta: {
      onError: (error: Error) => {
        console.error("Query error:", error);
        toast.error("Failed to load profile data");
      }
    }
  });

  useEffect(() => {
    // Check authentication status
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      }
    };

    checkAuth();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate('/login');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleGenerateTemplate = (platform: string) => {
    setGeneratingTemplate(true);
    // Simulate template generation
    setTimeout(() => {
      toast.success(`${platform} template generated successfully!`);
      setGeneratingTemplate(false);
    }, 1000);
  };

  const handleCopyProfileLink = () => {
    if (profile?.username) {
      const url = `${window.location.origin}/${profile.username}`;
      navigator.clipboard.writeText(url);
      toast.success("Profile link copied to clipboard!");
    }
  };

  if (isError) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <DashboardSidebar />
          <div className="flex-1 flex flex-col">
            <Header />
            <main className="flex-1 container max-w-7xl mx-auto px-6 py-8">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-gray-900">Error Loading Media Kit</h2>
                <p className="mt-2 text-sm text-gray-600">Please try refreshing the page.</p>
              </div>
            </main>
            <Footer />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 container max-w-7xl mx-auto px-6 py-8">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold">Media Kit</h1>
            </div>

            {/* Profile Link Section */}
            <div className="mb-8 p-4 border rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Your public profile:</span>
                  <span className="text-sm text-muted-foreground">
                    {profile?.username ? `${window.location.origin}/${profile.username}` : 'Loading...'}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyProfileLink}
                  disabled={!profile?.username}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
              </div>
            </div>
            
            <div className="space-y-6">
              <Tabs defaultValue="instagram" className="w-full">
                <TabsList>
                  <TabsTrigger value="instagram">Instagram</TabsTrigger>
                  <TabsTrigger value="tiktok">TikTok</TabsTrigger>
                  <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
                  <TabsTrigger value="facebook">Facebook</TabsTrigger>
                </TabsList>

                <TabsContent value="instagram" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Instagram Templates */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="aspect-square relative bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-lg overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Instagram className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold">Story Template</h3>
                        <p className="text-sm text-muted-foreground">Perfect for Instagram stories</p>
                        <Button 
                          onClick={() => handleGenerateTemplate('Instagram')}
                          disabled={generatingTemplate}
                          className="w-full"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Generate Template
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="tiktok" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* TikTok Templates */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="aspect-[9/16] relative bg-gradient-to-br from-[#00f2ea] to-[#ff0050] rounded-lg overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <TikTok className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold">Video Cover Template</h3>
                        <p className="text-sm text-muted-foreground">Optimized for TikTok video covers</p>
                        <Button 
                          onClick={() => handleGenerateTemplate('TikTok')}
                          disabled={generatingTemplate}
                          className="w-full"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Generate Template
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="linkedin" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* LinkedIn Templates */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="aspect-video relative bg-gradient-to-br from-[#0077b5] to-[#00a0dc] rounded-lg overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Linkedin className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold">Post Template</h3>
                        <p className="text-sm text-muted-foreground">Professional LinkedIn post design</p>
                        <Button 
                          onClick={() => handleGenerateTemplate('LinkedIn')}
                          disabled={generatingTemplate}
                          className="w-full"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Generate Template
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="facebook" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Facebook Cover Template */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="aspect-[1.91/1] relative bg-gradient-to-br from-[#1877f2] to-[#3b5998] rounded-lg overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Facebook className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold">Cover Template</h3>
                        <p className="text-sm text-muted-foreground">Optimized for Facebook covers</p>
                        <Button 
                          onClick={() => handleGenerateTemplate('Facebook Cover')}
                          disabled={generatingTemplate}
                          className="w-full"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Generate Template
                        </Button>
                      </div>
                    </div>

                    {/* Facebook Post Template */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="aspect-[1.91/1] relative bg-gradient-to-br from-[#1877f2] to-[#3b5998] rounded-lg overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Facebook className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold">Post Template</h3>
                        <p className="text-sm text-muted-foreground">Optimized for Facebook posts</p>
                        <Button 
                          onClick={() => handleGenerateTemplate('Facebook Post')}
                          disabled={generatingTemplate}
                          className="w-full"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Generate Template
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Media;
